import { Component, computed, input, output, signal, effect, viewChild, ElementRef } from '@angular/core';
import { ConnectedPosition, OverlayModule } from '@angular/cdk/overlay';
import { TranslateModule } from '@ngx-translate/core';
import { ButtonComponent } from '../button/button.component';

export interface ButtonListItem {
  key: string;
  label: string;
}

@Component({
  selector: 'app-button-list',
  standalone: true,
  imports: [OverlayModule, TranslateModule, ButtonComponent],
  templateUrl: './button-list.component.html',
  styleUrls: ['./button-list.component.scss'],
})
export class ButtonListComponent {
  /** All available items to show in the list */
  items = input.required<ButtonListItem[]>();

  /** Currently selected keys (controlled from parent) */
  selectedKeys = input<Set<string>>(new Set());

  /** Emits the new Set<string> whenever a checkbox is toggled */
  selectionChange = output<Set<string>>();

  /** Src of the icon displayed on the trigger button */
  iconSrc = input<string>('');

  /** Translation key for the panel header title */
  panelTitle = input<string>('');

  /** Minimum number of items that must stay selected (default 1) */
  minSelected = input<number>(1);

  protected isOpen = signal(false);
  protected internalKeys = signal<Set<string>>(new Set());

  readonly positions: ConnectedPosition[] = [
    {
      originX: 'end',
      originY: 'bottom',
      overlayX: 'end',
      overlayY: 'top',
      offsetY: 6,
    },
  ];

  constructor() {
    // Sync internal state whenever the parent passes a new Set
    effect(() => {
      this.internalKeys.set(new Set(this.selectedKeys()));
    });

    // Keep the select-all checkbox indeterminate property in sync
    effect(() => {
      const el = this.selectAllCheckbox()?.nativeElement;
      if (el) {
        el.indeterminate = this.isSomeSelected();
      }
    });
  }

  protected readonly selectAllCheckbox = viewChild<ElementRef<HTMLInputElement>>('selectAllCheckbox');

  protected isAllSelected = computed(() => this.internalKeys().size === this.items().length);
  protected isSomeSelected = computed(() =>
    this.internalKeys().size > 0 && this.internalKeys().size < this.items().length
  );

  protected toggleAll(): void {
    const newKeys = this.isAllSelected()
      ? new Set(this.items().slice(0, this.minSelected()).map(i => i.key))
      : new Set(this.items().map(i => i.key));
    this.internalKeys.set(newKeys);
    this.selectionChange.emit(new Set(newKeys));
  }

  protected toggleOpen(): void {
    this.isOpen.set(!this.isOpen());
  }

  protected close(): void {
    this.isOpen.set(false);
  }

  protected toggle(key: string): void {
    const current = new Set(this.internalKeys());
    if (current.has(key)) {
      if (current.size > this.minSelected()) {
        current.delete(key);
      }
    } else {
      current.add(key);
    }
    this.internalKeys.set(current);
    this.selectionChange.emit(new Set(current));
  }

  protected isVisible(key: string): boolean {
    return this.internalKeys().has(key);
  }
}
